import React, { useState, useCallback } from 'react';

type GistExportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  files: Record<string, string>;
  projectName: string;
};

type GistResult = {
  id: string;
  html_url: string;
  files: Record<string, { filename: string; raw_url: string }>;
};

export const GistExportModal = React.memo(function GistExportModal({
  isOpen,
  onClose,
  files,
  projectName,
}: GistExportModalProps) {
  const [token, setToken] = useState('');
  const [description, setDescription] = useState(`${projectName} - Exported from Atlas IDE`);
  const [isPublic, setIsPublic] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GistResult | null>(null);

  const fileEntries = Object.entries(files);
  const totalSize = fileEntries.reduce((sum, [, content]) => sum + content.length, 0);

  const handleExport = useCallback(async () => {
    if (!token.trim()) {
      setError('Please enter a GitHub personal access token');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Prepare files for gist - flatten paths by replacing / with _
      const gistFiles: Record<string, { content: string }> = {};
      for (const [path, content] of fileEntries) {
        // GitHub Gist doesn't support nested paths, so we flatten them
        const flattenedName = path.replace(/\//g, '_');
        gistFiles[flattenedName] = { content };
      }

      const response = await fetch('https://api.github.com/gists', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/vnd.github+json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          description,
          public: isPublic,
          files: gistFiles,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create gist');
    } finally {
      setLoading(false);
    }
  }, [token, description, isPublic, fileEntries]);

  const handleClose = useCallback(() => {
    setResult(null);
    setError(null);
    onClose();
  }, [onClose]);

  const handleCopyUrl = useCallback(() => {
    if (result?.html_url) {
      navigator.clipboard.writeText(result.html_url);
    }
  }, [result]);

  if (!isOpen) return null;

  return (
    <div className="modal-backdrop" onClick={handleClose}>
      <div
        className="modal gist-export-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '500px' }}
      >
        <div className="modal-header">
          <div className="modal-title">Export to GitHub Gist</div>
          <button className="modal-close" onClick={handleClose}>
            x
          </button>
        </div>

        <div className="modal-body" style={{ padding: '16px' }}>
          {result ? (
            <div className="gist-success">
              <div style={{ color: 'var(--success)', fontSize: '18px', marginBottom: '12px' }}>
                Gist created successfully!
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                  Gist URL:
                </label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    readOnly
                    value={result.html_url}
                    style={{
                      flex: 1,
                      padding: '8px',
                      background: 'var(--bg-secondary)',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      color: 'var(--text)',
                      fontSize: '13px',
                    }}
                  />
                  <button className="pill-btn" onClick={handleCopyUrl}>
                    Copy
                  </button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                <a
                  href={result.html_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pill-btn"
                  style={{ textDecoration: 'none' }}
                >
                  Open in GitHub
                </a>
                <button className="pill-btn" onClick={handleClose}>
                  Close
                </button>
              </div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                  GitHub Personal Access Token:
                </label>
                <input
                  type="password"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="ghp_xxxxxxxxxxxx"
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    color: 'var(--text)',
                    fontSize: '13px',
                  }}
                />
                <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: '4px' }}>
                  Requires 'gist' scope. <a href="https://github.com/settings/tokens/new?scopes=gist" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>Create token</a>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', color: 'var(--muted)' }}>
                  Description:
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Gist description"
                  style={{
                    width: '100%',
                    padding: '8px',
                    background: 'var(--bg-secondary)',
                    border: '1px solid var(--border)',
                    borderRadius: '4px',
                    color: 'var(--text)',
                    fontSize: '13px',
                  }}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={(e) => setIsPublic(e.target.checked)}
                  />
                  <span style={{ fontSize: '13px' }}>Make gist public</span>
                </label>
              </div>

              <div style={{ marginBottom: '16px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '4px' }}>
                <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '8px' }}>Files to export:</div>
                <div style={{ fontSize: '13px' }}>
                  {fileEntries.length} file{fileEntries.length !== 1 ? 's' : ''} ({(totalSize / 1024).toFixed(1)} KB)
                </div>
                <div style={{ maxHeight: '120px', overflow: 'auto', marginTop: '8px' }}>
                  {fileEntries.slice(0, 10).map(([path]) => (
                    <div key={path} style={{ fontSize: '11px', color: 'var(--muted)', padding: '2px 0' }}>
                      {path}
                    </div>
                  ))}
                  {fileEntries.length > 10 && (
                    <div style={{ fontSize: '11px', color: 'var(--muted)', fontStyle: 'italic' }}>
                      ... and {fileEntries.length - 10} more
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div style={{ color: 'var(--error)', fontSize: '12px', marginBottom: '12px' }}>
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {!result && (
          <div className="modal-footer">
            <button className="pill-btn" onClick={handleClose}>
              Cancel
            </button>
            <button
              className="pill-btn"
              onClick={handleExport}
              disabled={loading || !token.trim()}
              style={{ opacity: loading || !token.trim() ? 0.5 : 1 }}
            >
              {loading ? 'Creating...' : 'Create Gist'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

export default GistExportModal;
